import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { adoptUiScale, coerceUiScale, getUiScale, subscribeToUiScale } from '@/lib/uiScale';
import { UI_SCALES, UI_SCALE_LABELS, type UiScale } from '@shared/enums.js';

/**
 * Interface scale, in the account menu.
 *
 * It belongs here rather than in Settings because it is a property of the
 * person, not of the CRM: the admin and the VA share one database and one
 * install, and their eyesight is not a shared configuration value. Settings is
 * also admin-only, which would have put a VA's own font size out of their reach.
 *
 * Applied immediately and optimistically — a scale change you have to wait for
 * feels broken, and there is nothing destructive about it. If the save fails,
 * the previous value is put back and the failure is said out loud rather than
 * leaving someone at a size that will not survive their next sign-in.
 */
export function UiScaleSelector({
  /**
   * Whether to persist the choice to the signed-in user's preferences.
   *
   * False in anonymous mode — which is how LeadForge runs today, with
   * enforcement off. There is no account to attach a preference to, and
   * inventing an anonymous user row to hold a font size would be a data model
   * for a problem nobody has. The localStorage mirror is enough: it survives a
   * reload, and it is exactly what the login page reads.
   */
  persistToServer,
}: {
  persistToServer: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();

  // Read from the one observable, so the menu always shows what is actually on
  // screen — including the moment AuthProvider replaces the local mirror with
  // the signed-in user's server preference.
  const scale = React.useSyncExternalStore(subscribeToUiScale, getUiScale, getUiScale);
  const setScale = adoptUiScale;

  const save = useMutation({
    mutationFn: (next: UiScale) => api.updateMyPreferences({ uiScale: next }),
    onSuccess: (preferences) => {
      // Trust the server's answer over the optimistic one.
      adoptUiScale(coerceUiScale(preferences.uiScale));
      queryClient.invalidateQueries({ queryKey: ['auth', 'preferences'] });
    },
    onError: (error: Error, _next, previous) => {
      // Roll back to what was on screen before, so the person is not left at a
      // size that silently reverts the next time they sign in.
      adoptUiScale(coerceUiScale(previous));
      toast.error(
        error instanceof ApiError
          ? `Could not save the interface scale: ${error.message}`
          : 'Could not save the interface scale.',
      );
    },
    onMutate: (next) => {
      const previous = scale;
      // Applied before the request, not after it.
      adoptUiScale(next);
      return previous;
    },
  });

  return (
    <label className="flex items-center gap-1.5 rounded px-1.5 py-1 text-2xs text-muted-foreground">
      <ZoomIn className="h-3 w-3 shrink-0" />
      <span className="shrink-0">Scale</span>
      <select
        aria-label="Interface scale"
        className="ml-auto rounded border border-border bg-surface px-1 py-0.5 text-2xs text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        value={scale}
        // Locked while a save is in flight. Two overlapping PATCHes can land
        // out of order, and the older response would then overwrite the newer
        // intent — the person ends up at a scale they did not pick last. The
        // requests take milliseconds on loopback, so a disabled control for
        // that long is invisible; a queue or sequence numbers would be a lot of
        // machinery for a problem a disabled attribute solves outright.
        //
        // Anonymous mode is never disabled: nothing is written, so nothing can
        // race.
        disabled={persistToServer && save.isPending}
        onChange={(event) => {
          const next = coerceUiScale(event.target.value);
          if (!persistToServer) {
            // Local only. No request, so no failure to roll back from.
            setScale(next);
            return;
          }
          // Belt to the disabled attribute's braces: a programmatic change or a
          // browser that fires one more event must not start a second write.
          if (save.isPending) return;
          save.mutate(next);
        }}
      >
        {UI_SCALES.map((option) => (
          <option key={option} value={option}>
            {UI_SCALE_LABELS[option]}
          </option>
        ))}
      </select>
    </label>
  );
}
